import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { acceptInvite, getInviteByToken } from '@/server/organizerInvite';
import { checkOrganizerAdminStatus } from '@/server/organizer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Navbar } from '@/components/ui/navbar';
import { CheckCircle2, XCircle, Clock, Info } from 'lucide-react';

export const Route = createFileRoute('/invite/$token')({
  component: InviteAcceptPage,
  loader: async ({ params }) => {
    return getInviteByToken({ data: params.token });
  },
});

/**
 * Invite acceptance page. Displays invite details and handles
 * auto-accepting for logged-in users, or prompts login/signup
 * for unauthenticated users.
 */
function InviteAcceptPage() {
  const invite = Route.useLoaderData();
  const { token } = Route.useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isAccepting, setIsAccepting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    error?: string;
    organizerId?: string;
    organizerName?: string;
  } | null>(null);

  useEffect(() => {
    if (!profile || result || isAccepting) return;
    if (!invite.found) return;
    if (invite.status !== 'pending' || invite.isExpired) return;

    const doAccept = async () => {
      setIsAccepting(true);
      try {
        // Check if already a member before attempting to accept
        const membership = await checkOrganizerAdminStatus({
          data: invite.organizerId,
        });
        if (membership.isAdmin) {
          setResult({
            success: false,
            error: 'already_admin',
            organizerId: invite.organizerId,
            organizerName: invite.organizerName,
          });
          return;
        }

        const res = await acceptInvite({ data: token });
        setResult(res);
      } catch (err) {
        setResult({
          success: false,
          error: err instanceof Error ? err.message : 'Failed to accept invite',
        });
      } finally {
        setIsAccepting(false);
      }
    };

    doAccept();
  }, [profile, invite, token, result, isAccepting]);

  if (!invite.found) {
    return (
      <InviteLayout>
        <StatusCard
          icon={<XCircle className="w-10 h-10 text-red-500" />}
          title="Invite Not Found"
          description="This invite link is invalid or has been removed. Please ask the organization admin for a new invite."
        />
      </InviteLayout>
    );
  }

  if (invite.status === 'accepted') {
    return (
      <InviteLayout>
        <StatusCard
          icon={<CheckCircle2 className="w-10 h-10 text-green-500" />}
          title="Already Accepted"
          description="This invite has already been accepted."
          action={
            <Link to="/">
              <Button>Go to Home</Button>
            </Link>
          }
        />
      </InviteLayout>
    );
  }

  if (invite.status === 'revoked') {
    return (
      <InviteLayout>
        <StatusCard
          icon={<XCircle className="w-10 h-10 text-red-500" />}
          title="Invite Revoked"
          description="This invite has been revoked by the organization admin. Please contact them if you believe this is an error."
        />
      </InviteLayout>
    );
  }

  if (invite.isExpired) {
    return (
      <InviteLayout>
        <InviteHeader
          organizerName={invite.organizerName}
          organizerLogoUrl={invite.organizerLogoUrl}
        />
        <StatusCard
          icon={<Clock className="w-10 h-10 text-amber-500" />}
          title="Invite Expired"
          description={`This invite to join ${invite.organizerName} has expired. Please ask the organization admin to send you a new invite.`}
        />
      </InviteLayout>
    );
  }

  if (isAccepting) {
    return (
      <InviteLayout>
        <InviteHeader
          organizerName={invite.organizerName}
          organizerLogoUrl={invite.organizerLogoUrl}
        />
        <Card className="max-w-md mx-auto">
          <CardContent className="py-12 flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500">
              Accepting invite...
            </p>
          </CardContent>
        </Card>
      </InviteLayout>
    );
  }

  // Accepted successfully
  if (result?.success) {
    return (
      <InviteLayout>
        <InviteHeader
          organizerName={invite.organizerName}
          organizerLogoUrl={invite.organizerLogoUrl}
        />
        <StatusCard
          icon={<CheckCircle2 className="w-10 h-10 text-green-500" />}
          title="You're In!"
          description={`You've been added to ${result.organizerName}.`}
          action={
            <Button
              onClick={() =>
                navigate({
                  to: '/admin/dashboard/organizer/$organizerId',
                  params: { organizerId: result.organizerId! },
                })
              }
            >
              Go to Dashboard
            </Button>
          }
        />
      </InviteLayout>
    );
  }

  // Already a member
  if (result && !result.success && result.error === 'already_admin') {
    return (
      <InviteLayout>
        <InviteHeader
          organizerName={invite.organizerName}
          organizerLogoUrl={invite.organizerLogoUrl}
        />
        <StatusCard
          icon={<Info className="w-10 h-10 text-blue-500" />}
          title="Already a Member"
          description={`You're already a member of ${invite.organizerName}.`}
          action={
            <Button
              onClick={() =>
                navigate({
                  to: '/admin/dashboard/organizer/$organizerId',
                  params: { organizerId: result.organizerId! },
                })
              }
            >
              Go to Dashboard
            </Button>
          }
        />
      </InviteLayout>
    );
  }

  // Accept failed
  if (result && !result.success) {
    const errorMessages: Record<string, string> = {
      not_found: 'This invite could not be found.',
      already_accepted: 'This invite has already been accepted.',
      revoked: 'This invite has been revoked.',
      expired:
        'This invite has expired. Please ask the organization admin to send you a new invite.',
      email_mismatch:
        'This invite was sent to a different email address. Please sign in with the email address the invite was sent to.',
    };

    return (
      <InviteLayout>
        <InviteHeader
          organizerName={invite.organizerName}
          organizerLogoUrl={invite.organizerLogoUrl}
        />
        <StatusCard
          icon={<XCircle className="w-10 h-10 text-red-500" />}
          title="Could Not Accept Invite"
          description={
            errorMessages[result.error || ''] ||
            result.error ||
            'Something went wrong.'
          }
        />
      </InviteLayout>
    );
  }

  // Not logged in — prompt to login or sign up
  const redirectPath = `/invite/${token}`;
  return (
    <InviteLayout>
      <InviteHeader
        organizerName={invite.organizerName}
        organizerLogoUrl={invite.organizerLogoUrl}
      />
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            You've Been Invited
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Sign in or create an account to join{' '}
            <span className="font-medium">{invite.organizerName}</span>.
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/login" search={{ redirect: redirectPath }}>
              <Button className="w-full">Sign In</Button>
            </Link>
            <Link to="/login" search={{ redirect: redirectPath }}>
              <Button variant="outline" className="w-full">
                Create Account
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </InviteLayout>
  );
}

/** Shared page layout for invite pages. */
function InviteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">{children}</div>
    </div>
  );
}

/** Displays organization name and logo above the invite card. */
function InviteHeader({
  organizerName,
  organizerLogoUrl,
}: {
  organizerName: string;
  organizerLogoUrl: string | null;
}) {
  return (
    <div className="flex flex-col items-center mb-6">
      {organizerLogoUrl ? (
        <img
          src={organizerLogoUrl}
          alt={`${organizerName} logo`}
          className="w-16 h-16 rounded-xl object-cover mb-3"
        />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center mb-3">
          <span className="text-xl font-semibold text-gray-500">
            {organizerName.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      <p className="text-sm text-gray-500">{organizerName}</p>
    </div>
  );
}

/** Reusable status card with icon, title, description, and optional action. */
function StatusCard({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <Card className="max-w-md mx-auto">
      <CardContent className="p-6 text-center">
        <div className="flex justify-center mb-4">{icon}</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {title}
        </h2>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        {action}
      </CardContent>
    </Card>
  );
}
